import { Box, BoxProps, styled, Switch } from "@mui/material";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { SenecSwitchProps } from "src/components/inputs/senec-switch/SenecSwitch";
import { merge } from "lodash-es";

type ThemeWithProps = {
    theme: SenecBaseTheme;
    props: SenecSwitchProps;
};

export const StyledSwitch = styled(Switch, {
    shouldForwardProp: (prop) => prop !== "hasError",
})<SenecSwitchProps>(({ hasError, ...props }) => {
    const themeWithProps = { theme: props.theme as SenecBaseTheme, props: props };
    return merge(
        baseSwitch(themeWithProps),
        filledSwitch(themeWithProps),
        outlinedSwitch(themeWithProps),
        disabledSwitch(),
        checkedSwitch(themeWithProps),
        errorSwitch(themeWithProps, hasError),
    );
});

const baseSwitch = (themeWithProps: ThemeWithProps) => {
    const theme = themeWithProps.theme;

    return merge(
        {
            padding: 0,
            borderRadius: 30,
            outline: `1px solid ${theme.palette.neutralGray30.main}`,
            "& .MuiSwitch-switchBase": {
                padding: 2,
                left: 1,
                top: 2,
            },
            "& .MuiSwitch-switchBase:hover": {
                backgroundColor: "transparent",
            },
            "& .MuiSwitch-thumb": {
                boxShadow: "none",
                transition: theme.transitions.create(["width"], {
                    duration: 100,
                }),
            },
            "& .MuiSwitch-track": {
                opacity: "1.0 !important",
            },
        },
        mediumBaseSwitch(themeWithProps),
        smallBaseSwitch(themeWithProps),
    );
};

const mediumBaseSwitch = ({ theme, props }: ThemeWithProps) => {
    return (
        props.size === "medium" && {
            width: 56,
            height: 30,
            /* :active deals with the toggle state of the switch i.e. when the switch is clicked/toggled.
             **  .Mui-checked deals with the current active (toggled) state of the switch i.e. when the switch is checked.
             */
            "&:active": {
                "& .MuiSwitch-thumb": {
                    width: 25,
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                    transform: "translateX(22px)",
                    "& .MuiSwitch-thumb": {
                        width: 28,
                    },
                },
            },
            "& .MuiSwitch-switchBase": {
                top: 3,
                "&.Mui-checked": {
                    transform: "translateX(26px)",
                },
            },
            "& .MuiSwitch-thumb": {
                width: 20,
                height: 20,
                borderRadius: 20,
                boxShadow: "none",
                transition: theme.transitions.create(["width"], {
                    duration: 100,
                }),
            },
        }
    );
};

const smallBaseSwitch = ({ props }: ThemeWithProps) => {
    return (
        props.size === "small" && {
            width: 40,
            height: 20,
            "&:active": {
                "& .MuiSwitch-thumb": {
                    width: 15,
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                    transform: "translateX(18px)",
                    "& .MuiSwitch-thumb": {
                        width: 15,
                    },
                },
            },
            "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                    transform: "translateX(21px)",
                },
            },
            "& .MuiSwitch-thumb": {
                width: 12,
                height: 12,
                borderRadius: 12,
            },
        }
    );
};

const filledSwitch = ({ theme, props }: ThemeWithProps) => {
    return (
        props.variant === "filled" && {
            "& .MuiSwitch-switchBase": {
                color: theme.palette.neutralGray70.main + "!important",
                "&.Mui-checked": {
                    color: theme.palette.white.main + "!important",
                    "& + .MuiSwitch-track": {
                        backgroundColor: theme.palette.deepBlue.main,
                    },
                },
            },
            "& .MuiSwitch-track": {
                backgroundColor: theme.palette.neutralGray000.main,
            },
            "&:not(:has(.Mui-disabled)):hover": {
                outline: `1px solid ${theme.palette.neutralGray40.main}`,
                "& .Mui-checked +.MuiSwitch-track": {
                    backgroundColor: theme.palette.azur50.main,
                },
                "& .MuiSwitch-track": {
                    backgroundColor: theme.palette.neutralGray05.main,
                },
            },
        }
    );
};

const outlinedSwitch = ({ theme, props }: ThemeWithProps) => {
    return (
        props.variant === "outlined" && {
            "& .MuiSwitch-switchBase": {
                color: theme.palette.deepBlue.main + "!important",
            },
            "& .MuiSwitch-track": {
                backgroundColor: theme.palette.white.main + "!important",
            },
            "&:not(:has(.Mui-disabled)):hover": {
                outline: "2px solid " + theme.palette.azur50.main + "!important",
            },
        }
    );
};

const disabledSwitch = () => {
    return {
        "&:has(.Mui-disabled)": {
            opacity: "0.5",
        },
    };
};

const checkedSwitch = ({ props }: ThemeWithProps) => {
    return merge(
        {
            "&:has(.Mui-checked)": {
                outline: "0 !important",
            },
        },
        mediumCheckedSwitch(props),
    );
};

const mediumCheckedSwitch = ({ size }: SenecSwitchProps) => {
    return (
        size === "medium" && {
            "&:has(.Mui-checked)": {
                "& .MuiSwitch-switchBase": {
                    top: 1,
                },
                "& .MuiSwitch-thumb": {
                    width: 24,
                    height: 24,
                    borderRadius: 24,
                },
            },
        }
    );
};

const errorSwitch = ({ props }: ThemeWithProps, hasError?: boolean) => {
    return (
        hasError &&
        props.variant === "outlined" && {
            pointerEvents: "none",
        }
    );
};

export const StyledSwitchBorder = styled(Box, {
    shouldForwardProp: (prop) => prop !== "hasError",
})<BoxProps & { hasError: boolean }>(({ theme, hasError }) => {
    const baseTheme = theme as SenecBaseTheme;
    return merge(baseBorder(), focusedBorder(baseTheme), errorBorder(baseTheme, hasError));
});

const baseBorder = () => {
    return {
        display: "flex",
        outlineStyle: "solid",
        outlineWidth: 2,
        outlineOffset: 3,
        outlineColor: "transparent",
        borderRadius: 32,
        marginRight: 8,
    };
};

const focusedBorder = (theme: SenecBaseTheme) => {
    return {
        "&:has(.MuiSwitch-switchBase.Mui-focusVisible)": {
            outlineColor: theme.palette.azur50.main,
        },
    };
};

const errorBorder = (theme: SenecBaseTheme, hasError: boolean) => {
    return (
        hasError && {
            outlineColor: theme.palette.red.main,
            "&:has(.MuiSwitch-switchBase.Mui-focusVisible)": null,
        }
    );
};
